// CD: backend
// CD: wp/wordpress.css.
import { defineOnce, fetchSectionDocument, showToast, emit, EVENTS, routesRoot, placePopover, surfaceMotion, motionDisclosure, closeMotionDisclosure, activateLayer, releaseLayer } from './pushe-ui.js';
const CART_ADDED = 'pushe:cart:added';
import { getQuickLocks, publishQuickState } from './pushe-quick-add-state.js';

export function filterFormToUrl(pathname, formData, sortBy) {
  const pairs = Array.from(formData ?? []).filter(([key, value]) =>
    (key.startsWith('filter.') || key === 'q' || key === 'sort_by') && typeof value === 'string' && value.trim()
  ).map(([key, value]) => [key, value.trim()]);
  if (typeof sortBy === 'string' && sortBy.trim()) {
    for (let i = pairs.length - 1; i >= 0; i--) if (pairs[i][0] === 'sort_by') pairs.splice(i, 1);
    pairs.push(['sort_by', sortBy.trim()]);
  }
  pairs.sort(([a], [b]) => a < b ? -1 : a > b ? 1 : 0);
  const query = pairs.map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`).join('&');
  return pathname + (query ? `?${query}` : '');
}

export function activeFilterCount(searchParams) {
  const ranges = new Set();
  let count = 0;
  for (const [key, value] of new URLSearchParams(searchParams ?? '')) {
    if (!key.startsWith('filter.') || !value.trim()) continue;
    if (/\.(gte|lte)$/.test(key)) ranges.add(key.replace(/\.(gte|lte)$/, ''));
    else count++;
  }
  return count + ranges.size;
}

export function shouldEnableQuickAdd() { return true; }

export const postAdd = async (id, options) => (await import('./pushe-quick-add.js')).postAdd(id, options);

function formEntries(form) {
  return Array.from(form?.querySelectorAll('input[name],select[name]') ?? []).filter(input =>
    !input.disabled && (!['checkbox', 'radio'].includes(input.type) || input.checked)
  ).map(input => [input.name || input.getAttribute('name'), input.value]);
}

export class PusheCollection extends HTMLElement {
  connectedCallback() {
    if (this.connected) return;
    this.connected = true;
    this.positionFrame = null;
    this.requests ??= new Set();
    this.quickLocks ??= getQuickLocks(this.ownerDocument);
    this.loadedUrl ??= window.location.href;
    this.quick = new Map();
    this.onClick = event => {
      const summary = event.target.closest?.('.pushe-filter__group > summary');
      if (summary && !summary.parentElement.open) this.claimFilterGroup(summary.parentElement);
      this.handleClick(event);
    };
    this.onChange = event => this.handleChange(event);
    this.onQuickState = ({detail}) => {
      if (detail.source === this) return;
      const wrappers = [...this.querySelectorAll('[data-pushe-quick]'), ...(this.originalGrid?.querySelectorAll('[data-pushe-quick]') || [])];
      for (const wrapper of wrappers) {
        const item = this.quick.get(wrapper.dataset.pusheQuick)?.find(value => String(value.variant_id) === String(detail.item.variant_id));
        if (item) this.quickState(wrapper, detail.state, item);
      }
      this.applyQuickLocks();
    };
    this.onSubmit = event => { if (event.target.matches('[data-pushe-facets]')) { event.preventDefault(); this.applyFilters(); } };
    this.onPop = () => this.navigate(window.location.href, false);
    this.onKey = event => this.sortKey(event);
    this.onGroupEscape = event => {
      if (event.key === 'Escape' && event.target.closest?.('.pushe-filter__group[open]')) {
        event.preventDefault();
        event.stopPropagation();
        this.closeFilterGroups(true);
      }
    };
    this.onOutside = event => {
      if (!this.sortWrap?.contains(event.target)) this.closeSort();
      for (const group of this.querySelectorAll('.pushe-filter__group[open]')) {
        if (!group.contains(event.target)) closeMotionDisclosure(group);
      }
    };
    this.onFacetToggle = event => {
      const group = event.target;
      if (!group.matches('.pushe-filter__group')) return;
      if (!group.open) { releaseLayer(group); return; }
      this.claimFilterGroup(group);
      placePopover(group.querySelector('summary'), group.querySelector(':scope > div'), { inline: !window.matchMedia?.('(min-width: 900px)').matches });
    };
    this.onFacetKey = event => {
      if (!event.defaultPrevented && event.key === 'Escape') this.closeFilterGroups(true);
    };
    this.onPosition = () => {
      if (this.sortPanel && !this.sortPanel.hidden) this.placeSort();
      for (const group of this.querySelectorAll('.pushe-filter__group')) {
        const panel = group.querySelector(':scope > div');
        if (window.matchMedia?.('(min-width: 900px)').matches) {
          if (group.open) placePopover(group.querySelector('summary'), panel);
        } else panel?.removeAttribute('style');
      }
    };
    this.onFocus = event => { if (event.target === this.sortSelect && this.sortTrigger && !this.sortTrigger.hidden) this.sortTrigger.focus(); };
    this.addEventListener('click', this.onClick);
    this.addEventListener('change', this.onChange);
    this.addEventListener('submit', this.onSubmit);
    this.addEventListener('keydown', this.onKey);
    this.addEventListener('keydown', this.onGroupEscape, true);
    this.addEventListener('focusin', this.onFocus);
    this.addEventListener('toggle', this.onFacetToggle, true);
    window.addEventListener('popstate', this.onPop);
    this.ownerDocument.addEventListener('pointerdown', this.onOutside);
    this.ownerDocument.addEventListener('pushe:quick:state', this.onQuickState);
    this.ownerDocument.addEventListener('keydown', this.onFacetKey);
    this.queuePosition=()=>{if(this.positionFrame!=null)return;this.positionFrame=window.requestAnimationFrame(()=>{this.positionFrame=null;if(this.connected)this.onPosition();});};
    window.addEventListener('resize', this.onPosition);
    window.addEventListener('scroll', this.queuePosition, true);
    window.visualViewport?.addEventListener('resize', this.onPosition);
    window.visualViewport?.addEventListener('scroll', this.queuePosition);
    this.media = ['(min-width: 600px)', '(hover:hover)', '(min-width: 900px)', '(min-width: 900px) and (pointer: fine)']
      .map(query => window.matchMedia?.(query)).filter(Boolean);
    this.onMedia = () => this.refreshMedia();
    for (const media of this.media) media.addEventListener?.('change', this.onMedia);
    this.prepare();
  }

  disconnectedCallback() {
    if (!this.connected) return;
    this.connected = false;
    window.cancelAnimationFrame?.(this.positionFrame);
    this.positionFrame = null;
    this.disclosureCleanups?.forEach(clean=>clean());
    this.sequence = (this.sequence ?? 0) + 1;
    this.navigation?.abort();
    clearTimeout(this.navigationTimer);
    this.restoreGrid();
    if (this.navigation) {
      this.restoreControls();
      if (window.location.href !== this.loadedUrl) window.history.replaceState(window.history.state, '', this.loadedUrl);
      this.navigation = null;
    }
    this.removeAttribute('aria-busy');
    for (const controller of this.requests) controller.abort();
    for (const media of this.media) media.removeEventListener?.('change', this.onMedia);
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('change', this.onChange);
    this.removeEventListener('submit', this.onSubmit);
    this.removeEventListener('keydown', this.onKey);
    this.removeEventListener('keydown', this.onGroupEscape, true);
    this.removeEventListener('focusin', this.onFocus);
    this.removeEventListener('toggle', this.onFacetToggle, true);
    window.removeEventListener('popstate', this.onPop);
    this.ownerDocument.removeEventListener('pointerdown', this.onOutside);
    this.ownerDocument.removeEventListener('pushe:quick:state', this.onQuickState);
    this.ownerDocument.removeEventListener('keydown', this.onFacetKey);
    window.removeEventListener('resize', this.onPosition);
    window.removeEventListener('scroll', this.queuePosition, true);
    window.visualViewport?.removeEventListener('resize', this.onPosition);
    window.visualViewport?.removeEventListener('scroll', this.queuePosition);
    this.querySelector('[data-pushe-filter-dialog]')?.close?.();
    this.closeSort(false, true);
  }

  prepare() {
    this.disclosureCleanups?.forEach(clean=>clean());
    this.disclosureCleanups=[...this.querySelectorAll('.pushe-filter__group')].map(group=>{
      const clean = motionDisclosure(group,group.querySelector(':scope > div'));
      return () => { releaseLayer(group); closeMotionDisclosure(group, { instant: true }); clean(); };
    });
    this.setAttribute('data-enhanced', '');
    this.querySelector('.pushe-colhead__title')?.setAttribute('tabindex', '-1');
    for (const input of this.querySelectorAll('[data-pushe-facets] input[type="number"]')) {
      input.min = '0';
    }
    for (const island of this.querySelectorAll('script[data-pushe-quick-add]')) {
      const uid = island.getAttribute('data-pushe-quick-add');
      try { this.quick.set(uid, JSON.parse(island.textContent)); } catch { this.quick.set(uid, []); }
    }
    this.sortWrap = this.querySelector('[data-pushe-desktop-bar] .pushe-filter__select');
    this.sortSelect = this.sortWrap?.querySelector('select');
    const ui = this.querySelector('[data-pushe-sort-ui]');
    if (ui && this.sortWrap) { this.sortWrap.append(...ui.childNodes); ui.remove(); }
    this.sortTrigger = this.sortWrap?.querySelector('.pushe-dd__trigger');
    this.sortPanel = this.sortWrap?.querySelector('.pushe-dd__panel');
    this.updateCount(new URL(window.location.href).searchParams);
    this.refreshMedia();
    const sheet = this.querySelector('.pushe-filter__sheet');
    if (sheet && window.location.hash === `#${sheet.id}`) {
      const url = new URL(window.location.href);
      url.hash = '';
      window.history.replaceState(window.history.state, '', url.href);
      this.loadedUrl = url.href;
      if (!window.matchMedia?.('(min-width: 900px)').matches) {
        this.querySelector('[data-pushe-filter-dialog]')?.open?.(this.querySelector('.pushe-filter__open'));
      }
    }
  }

  updateCount(params) {
    const button = this.querySelector('.pushe-filter__open');
    if (button) (button.querySelector('.pushe-press-ink') ?? button).textContent = (this.dataset.filterLabel ?? '').replace('{n}', String(activeFilterCount(params)));
  }

  restoreGrid() {
    if (this.loadingGrid?.parentNode && this.originalGrid) this.loadingGrid.replaceWith(this.originalGrid);
    this.loadingGrid = null;
    this.originalGrid = null;
    this.applyQuickLocks();
  }

  restoreControls() {
    const form = this.querySelector('[data-pushe-facets]');
    form?.reset?.();
    for (const input of form?.querySelectorAll('input') ?? []) {
      if (['checkbox', 'radio'].includes(input.type)) input.checked = input.hasAttribute('checked');
      else input.value = input.getAttribute('value') ?? '';
    }
    for (const select of this.querySelectorAll('.pushe-filter__sort')) {
      const selected = select.querySelector('option[selected]') ?? select.querySelector('option');
      if (selected) select.value = selected.value;
    }
    this.syncSortLabels();
  }

  syncSortLabels() {
    for (const select of this.querySelectorAll('.pushe-filter__sort')) {
      for (const label of select.parentElement.querySelectorAll('[data-pushe-sort-label]')) {
        label.classList.toggle('is-selected', label.getAttribute('data-pushe-sort-label') === select.value);
      }
    }
  }

  quickState(wrapper, state, item) {
    const current = wrapper.querySelector('.pushe-quick');
    if (!current) return;
    const focused = current.contains(this.ownerDocument.activeElement);
    if (state === 'pending' && focused) wrapper.quickFocus = this.ownerDocument.activeElement;
    const node = wrapper.querySelector(`template[data-pushe-quick-state="${state}"]`)?.content.firstElementChild?.cloneNode(true);
    if (node) current.replaceChildren(...node.childNodes);
    current.dataset.state = state;
    if (state === 'pending') {
      current.setAttribute('aria-busy', 'true');
      current.querySelector('.pushe-quick__group,.pushe-quick__single')?.setAttribute('aria-busy', 'true');
    } else {
      current.removeAttribute('aria-busy');
      current.querySelector('[aria-busy]')?.removeAttribute('aria-busy');
    }
    for (const control of current.querySelectorAll('input,button')) {
      control.disabled = state === 'pending';
      if (control.type === 'radio') control.checked = control.value === item.label;
    }
    const retry = current.querySelector('.pushe-quick__retry');
    if (retry) {
      if (node) (retry.querySelector('.pushe-press-ink') ?? retry).textContent += ` · ${item.label.toLowerCase()}`;
      retry.disabled = state === 'pending' || this.quickLocks.get(String(item.variant_id))?.state === 'uncertain';
    }
    if (state !== 'pending' && wrapper.quickFocus) {
      const active = this.ownerDocument.activeElement;
      if (focused || active === this.ownerDocument.body || active === wrapper.quickFocus) {
        (current.querySelector('button:not(:disabled)') ?? wrapper.closest('.pushe-card')?.querySelector('.pushe-card__name'))?.focus({ preventScroll: true });
      }
      delete wrapper.quickFocus;
    }
  }

  applyQuickLocks() {
    for (const wrapper of this.querySelectorAll('[data-pushe-quick]')) {
      const items = this.quick.get(wrapper.dataset.pusheQuick) ?? [];
      const item = items.find(value => this.quickLocks.has(String(value.variant_id)));
      const state = item && this.quickLocks.get(String(item.variant_id)).state;
      delete wrapper.dataset.pending;
      delete wrapper.dataset.uncertain;
      if (!state) continue;
      wrapper.dataset[state] = 'true';
      wrapper.dataset.picked = item.label;
      this.quickState(wrapper, state === 'uncertain' ? 'error' : 'pending', item);
    }
  }

  refreshMedia() {
    const match = window.matchMedia?.bind(window);
    const quickEnabled = shouldEnableQuickAdd(match);
    for (const wrapper of this.querySelectorAll('[data-pushe-quick]')) {
      const items = this.quick.get(wrapper.dataset.pusheQuick) ?? [];
      wrapper.hidden = !quickEnabled || !items.some(item => item.variant_id != null);
      for (const radio of wrapper.querySelectorAll('input[type="radio"]')) {
        const item = items.find(value => value.label === radio.value);
        radio.setAttribute('aria-disabled', String(!item?.available));
        radio.closest('label')?.classList.toggle('pushe-quick__size--soldout', !item?.available);
      }
      wrapper.querySelector('.pushe-quick__single')?.setAttribute('aria-disabled', String(!items[0]?.available));
    }
    this.applyQuickLocks();
    const desktop = Boolean(match?.('(min-width: 900px)').matches);
    const dialog = this.querySelector('[data-pushe-filter-dialog]');
    if (desktop) dialog?.close?.();
    const form = this.querySelector('[data-pushe-facets]');
    const slot = this.querySelector(desktop ? '[data-pushe-facet-home]' : '[data-pushe-facet-slot]');
    if (form && slot && form.parentElement !== slot) {
      this.closeFilterGroups();
      slot.append(form);
    }
    const customSort = Boolean(match?.('(min-width: 900px) and (pointer: fine)').matches);
    if (this.sortTrigger) this.sortTrigger.hidden = !customSort;
    this.sortWrap?.classList.toggle('pushe-dd', customSort);
    if (this.sortSelect) {
      if (customSort) { this.sortSelect.setAttribute('aria-hidden', 'true'); this.sortSelect.setAttribute('tabindex', '-1'); }
      else { this.sortSelect.removeAttribute('aria-hidden'); this.sortSelect.removeAttribute('tabindex'); this.closeSort(); }
    }
  }

  applyFilters(sortBy) {
    const form = this.querySelector('[data-pushe-facets]');
    if (!form) return;
    if (sortBy !== undefined) {
      for (const select of this.querySelectorAll('.pushe-filter__sort')) select.value = sortBy;
    }
    this.syncSortLabels();
    const desktop = Boolean(window.matchMedia?.('(min-width: 900px)').matches);
    const select = this.querySelector(desktop ? '[data-pushe-desktop-bar] .pushe-filter__sort' : '[data-pushe-mobile-bar] .pushe-filter__sort');
    const currentSort = select?.value ?? sortBy;
    if (form.reportValidity && !form.reportValidity()) return;
    const url = filterFormToUrl(new URL(form.action || form.getAttribute('action'), window.location.href).pathname, formEntries(form), currentSort);
    return this.navigate(url);
  }

  handleChange(event) {
    const target = event.target;
    if (target.closest('[data-pushe-quick]')) {
      if (target.type !== 'radio') return;
      const wrapper = target.closest('[data-pushe-quick]');
      const item = this.quick.get(wrapper.dataset.pusheQuick)?.find(value => value.label === target.value);
      if (!item?.available || wrapper.dataset.pending || wrapper.dataset.uncertain) {
        event.preventDefault();
        for (const radio of wrapper.querySelectorAll('input[type="radio"]')) radio.checked = radio.value === wrapper.dataset.picked;
        return;
      }
      this.addQuick(wrapper, item);
    } else if (target.matches('.pushe-filter__sort')) this.applyFilters(target.value);
    else if (target.closest('[data-pushe-facets]')) this.applyFilters();
  }

  handleClick(event) {
    const target = event.target;
    const wrapper = target.closest('[data-pushe-quick]');
    if (wrapper) {
      const disabled = target.closest('[aria-disabled="true"],.pushe-quick__size--soldout');
      if (disabled || wrapper.dataset.pending || wrapper.dataset.uncertain) { event.preventDefault(); return; }
      const button = target.closest('.pushe-quick__single,.pushe-quick__retry');
      if (button) {
        const items = this.quick.get(wrapper.dataset.pusheQuick) ?? [];
        const item = button.matches('.pushe-quick__retry') ? items.find(value => value.label === wrapper.dataset.picked) : items[0];
        if (item?.available) this.addQuick(wrapper, item);
      }
      return;
    }
    if (target.closest('.pushe-filter__open')) {
      event.preventDefault();
      const button = target.closest('.pushe-filter__open');
      this.querySelector('[data-pushe-filter-dialog]')?.open?.(button);
    } else if (target.closest('.pushe-filter__close a')) {
      event.preventDefault();
    } else if (target.closest('.pushe-dd__trigger')) {
      event.preventDefault(); this.sortWrap?.classList.contains('is-open') ? this.closeSort() : this.openSort();
    } else if (target.closest('.pushe-dd__option')) {
      event.preventDefault();
      this.chooseSort(target.closest('.pushe-dd__option'));
    } else {
      const remove = target.closest('.pushe-filter__remove');
      const link = remove?.closest('li')?.querySelector('a') ?? target.closest('.pushe-filter__chiplabel[href],[data-pushe-clear],.pushe-pagination a');
      if (link && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey && (!event.button || event.button === 0)) {
        event.preventDefault(); this.navigate(link.getAttribute('href'));
      }
    }
  }

  async navigate(href, push = true) {
    const url = new URL(href, window.location.href);
    if (url.origin !== window.location.origin) return;
    this.navigation?.abort();
    clearTimeout(this.navigationTimer);
    this.restoreGrid();
    const controller = new AbortController();
    this.navigation = controller;
    const sequence = this.sequence = (this.sequence ?? 0) + 1;
    const results = this.querySelector('[data-pushe-results]');
    this.querySelector('[data-pushe-filter-dialog]')?.close?.();
    this.closeSort();
    this.setAttribute('aria-busy', 'true');
    const grid = results.querySelector('.pushe-grid, .pushe-empty');
    const skeleton = this.querySelector('template[data-pushe-loading]')?.content.firstElementChild?.cloneNode(true);
    if (grid && skeleton) {
      this.originalGrid = grid;
      this.loadingGrid = skeleton;
      grid.replaceWith(skeleton);
    }
    const timer = this.navigationTimer = setTimeout(() => controller.abort(), 12000);
    try {
      const doc = await fetchSectionDocument(this.dataset.sectionId, url.href, { signal: controller.signal });
      if (!this.connected || sequence !== this.sequence) return;
      if (controller.signal.aborted) throw new Error('section_timed_out');
      const nextRoot = Array.from(doc.querySelectorAll('pushe-collection')).find(node => node.dataset.sectionId === this.dataset.sectionId);
      const next = nextRoot?.querySelector('[data-pushe-results]');
      if (!next) throw new Error('section_missing');
      results.replaceWith(next);
      this.loadingGrid = null;
      this.originalGrid = null;
      if (push) window.history.pushState({}, '', url.pathname + url.search);
      this.loadedUrl = url.href;
      this.quick.clear();
      this.prepare();
      this.updateCount(url.searchParams);
      this.querySelector('.pushe-colhead__title')?.focus({ preventScroll: true });
      this.querySelector('[data-pushe-announcement]').textContent = next.dataset.countText;
    } catch {
      if (this.connected && sequence === this.sequence) {
        this.restoreGrid();
        this.restoreControls();
        showToast({ message: this.dataset.error, tone: 'error' });
        if (push) window.location.assign(url.href);
        else window.location.replace(url.href);
      }
    } finally {
      clearTimeout(timer);
      if (sequence === this.sequence) {
        this.removeAttribute('aria-busy');
        this.navigation = null;
      }
    }
  }

  async addQuick(wrapper, item) {
    const variantId = String(item.variant_id);
    if (this.quickLocks.has(variantId) || wrapper.dataset.pending || wrapper.dataset.uncertain || !item.available || !shouldEnableQuickAdd(window.matchMedia?.bind(window))) return;
    this.quickLocks.set(variantId, { state: 'pending' });
    wrapper.dataset.pending = 'true';
    wrapper.dataset.picked = item.label;
    this.applyQuickLocks();
    const controller = new AbortController();
    publishQuickState(item, 'pending', this, this.ownerDocument);
    this.requests.add(controller);
    const replaceState = state => {
      const wrappers = new Set([wrapper, ...this.querySelectorAll('[data-pushe-quick]'),
        ...(this.originalGrid?.querySelectorAll('[data-pushe-quick]') ?? [])]);
      for (const target of wrappers) {
        if (target !== wrapper && !this.quick.get(target.dataset.pusheQuick)?.some(value => String(value.variant_id) === variantId)) continue;
        target.dataset.picked = item.label;
        this.quickState(target, state, item);
      }
      publishQuickState(item, state, this, this.ownerDocument);
    };
    try {
      const cart = await postAdd(item.variant_id, { root: routesRoot(), signal: controller.signal });
      this.quickLocks.delete(variantId);
      emit(EVENTS.CART_UPDATED, { item_count: cart.item_count, source: 'add' });
      replaceState('added');
      if (this.connected) emit(CART_ADDED, { ...cart, opener: wrapper.closest('.pushe-card')?.querySelector('.pushe-card__name') });
      if (this.connected) showToast({ templateId: 'pushe-toast-added-template', vars: { name: wrapper.dataset.productTitle ?? '' } });
    } catch (error) {
      if (error.cart) emit(EVENTS.CART_UPDATED, { item_count: error.cart.item_count });
      if (error.uncertain) this.quickLocks.set(variantId, { state: 'uncertain' });
      else this.quickLocks.delete(variantId);
      if (error.uncertain) wrapper.dataset.uncertain = 'true';
      replaceState('error');
      if (this.connected) showToast({ message: error.uncertain ? this.dataset.error : (error.customerMessage || this.dataset.addError), tone: 'error' });
    } finally {
      delete wrapper.dataset.pending;
      this.applyQuickLocks();
      this.requests.delete(controller);
    }
  }

  openSort() {
    if (!this.sortPanel) return;
    activateLayer(this.sortPanel, ({ instant, restoreFocus }) => this.closeSort(restoreFocus, instant));
    this.sortPanel.hidden = false;
    surfaceMotion(this.sortPanel,true);
    this.sortWrap.classList.add('is-open');
    this.sortTrigger.setAttribute('aria-expanded', 'true');
    const options = [...this.sortPanel.querySelectorAll('[role="option"]')];
    for (const option of options) {
      const selected = option.dataset.value === this.sortSelect.value;
      option.setAttribute('aria-selected', String(selected));
      option.classList.toggle('is-selected', selected);
      const check = option.querySelector('.pushe-dd__check');
      if (check) check.hidden = !selected;
    }
    this.sortIndex = Math.max(0, options.findIndex(option => option.dataset.value === this.sortSelect.value));
    this.placeSort();
    this.pointSort();
    this.sortPanel.querySelector('[role="listbox"]')?.focus();
  }

  claimFilterGroup(group) {
    activateLayer(group, ({ instant, restoreFocus }) => {
      closeMotionDisclosure(group, { instant });
      if (restoreFocus) group.querySelector('summary')?.focus();
    });
  }

  closeFilterGroups(focus = false, instant = false) {
    for (const group of this.querySelectorAll('.pushe-filter__group[open]')) {
      closeMotionDisclosure(group, { instant });
      if (focus) group.querySelector('summary')?.focus();
    }
  }

  // All floating controls share the same intrinsic sizing and collision rule.
  placeSort() {
    placePopover(this.sortWrap, this.sortPanel);
  }

  closeSort(focus = false, instant = false) {
    if (!this.sortPanel) return;
    const panel=this.sortPanel;surfaceMotion(panel,false,()=>{panel.hidden=true;releaseLayer(panel);},{instant});
    this.sortWrap.classList.remove('is-open');
    this.sortTrigger?.setAttribute('aria-expanded', 'false');
    if (focus) this.sortTrigger?.focus();
  }

  pointSort() {
    const options = [...(this.sortPanel?.querySelectorAll('[role="option"]') ?? [])];
    options.forEach((option, index) => option.classList.toggle('is-active', index === this.sortIndex));
    const option = options[this.sortIndex];
    if (option) {
      this.sortPanel.querySelector('[role="listbox"]').setAttribute('aria-activedescendant', option.id);
      const list = this.sortPanel.querySelector('[role="listbox"]');
      if (option.offsetTop < list.scrollTop) list.scrollTop = option.offsetTop;
      else if (option.offsetTop + option.offsetHeight > list.scrollTop + list.clientHeight) list.scrollTop = option.offsetTop + option.offsetHeight - list.clientHeight;
    }
  }

  chooseSort(option) {
    this.sortSelect.value = option.dataset.value;
    this.closeSort(true);
    this.applyFilters(option.dataset.value);
  }

  sortKey(event) {
    if (!this.sortWrap?.contains(event.target) || this.sortTrigger?.hidden) return;
    const options = [...this.sortPanel.querySelectorAll('[role="option"]')];
    if (!options.length) return;
    if (event.key === 'Escape') { event.preventDefault(); this.closeSort(true); return; }
    if (event.key === 'Tab') { if (!this.sortPanel.hidden) this.closeSort(true); return; }
    if (['ArrowDown', 'ArrowUp', 'Home', 'End', 'PageDown', 'PageUp', 'Enter', ' '].includes(event.key)) {
      event.preventDefault();
      if (this.sortPanel.hidden) { this.openSort(); return; }
      if (event.key === 'Enter' || event.key === ' ') { this.chooseSort(options[this.sortIndex]); return; }
      const moves = { ArrowDown: 1, ArrowUp: -1, PageDown: 5, PageUp: -5 };
      this.sortIndex = event.key === 'Home' ? 0 : event.key === 'End' ? options.length - 1 : Math.max(0, Math.min(options.length - 1, this.sortIndex + moves[event.key]));
      this.sortPanel.classList.add('is-kbd'); this.pointSort();
    } else if (event.key?.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
      event.preventDefault();
      if (this.sortPanel.hidden) this.openSort();
      const now = Date.now();
      this.typeahead = now - (this.typeTime ?? 0) < 600 ? (this.typeahead ?? '') + event.key : event.key;
      this.typeTime = now;
      const repeated = [...this.typeahead].every(letter => letter === this.typeahead[0]);
      const needle = (repeated ? this.typeahead[0] : this.typeahead).toLowerCase();
      const start = repeated ? this.sortIndex + 1 : this.sortIndex;
      const offset = Array.from({ length: options.length }, (_, index) => (start + index) % options.length)
        .find(index => options[index].textContent.trim().toLowerCase().startsWith(needle));
      const index = offset ?? -1;
      if (index >= 0) { this.sortIndex = index; this.pointSort(); }
    }
  }
}

defineOnce('pushe-collection', PusheCollection);
